import { NgModule } from '@angular/core'
import { ExtraOptions, RouterModule, Routes } from '@angular/router'
import { BadgeEditorService } from '@app/modules/admin/components/badge-editor/services/badge-editor.service'
import { DCTSetupComponent } from '@app/modules/events/components/dct-setup/dct-setup.component'
import { EventLegalService } from '@app/modules/events/services/event-legal.service'
import { OrganizationSettingsComponent } from '@app/v2/settings/components/organization-settings/organization-settings.component'
import { industrySectorResolver } from '@app/v2/shared/resolvers/industry-sector.resolver'
import { AlmostThereComponent } from '@auth/components/almost-there/almost-there.component'
import { ConfirmEmailAlertComponent } from '@auth/components/confirmation-email/confirmation-email.component'
import { InvitationsTrackingComponent } from '@database/components/invitations-tracking/invitations-tracking.component'
import { CurrentWorkspaceGuard } from '@workspaces/guards/current-workspace.guard'
import { SsoCallbackComponent } from './app/components/sso-callback/sso-callback.component'
import { HomeRedirectComponent } from './app/components/home-redirect/home-redirect.component'
import { AdminGuard } from './app/guards/admin.guard'
import { AuthGuard } from './app/guards/auth.guard'
import { RoleGuard } from './app/guards/role.guard'
import { StatisticsComponent } from './app/modules/admin/components/statistics/statistics.component'
import { ToolsComponent } from './app/modules/admin/components/tools/tools.component'
import { DtcmSetupComponent } from './app/modules/events/components/dtcm-setup/dtcm-setup.component'
import { FinancialDashboardComponent } from './app/modules/events/components/financial-dashboard/financial-dashboard.component'
import { InviteComponent } from './app/modules/events/components/invite/invite.component'
import { DeleteFacebookDataCheckComponent } from './app/modules/settings/components/delete-facebook-data-check/delete-facebook-data-check.component'
import { OrganizationComponent } from './app/modules/settings/components/organization/organization.component'
import { ProfileComponent } from './app/modules/settings/components/profile/profile.component'
import { SettingsPageComponent } from './app/modules/settings/components/settings-page/settings-page.component'
import { NotFoundComponent } from './app/modules/shared/components/404/not-found.component'
import { BillingRedirectComponent } from './app/modules/subscriptions/components/billing-redirect/billing-redirect.component'
import { CancelledUpgradeComponent } from './app/modules/subscriptions/components/cancelled-upgrade/cancelled-upgrade.component'
import { SuccessfulUpgradeComponent } from './app/modules/subscriptions/components/successful-upgrade/successful-upgrade.component'
import { VenueCapacityPageComponent } from './app/modules/venue-capacity/components/venue-capacity-page/venue-capacity-page.component'
import { WorkspacesComponent } from './app/modules/workspaces/components/workspaces.component'
import { countriesResolver } from '@app/v2/shared/resolvers/countries.resolver'
import { EventsTimelineComponent } from '@app/v2/event/components/events-timeline/events-timeline.component'
import { UserRoles } from '@app/modules/auth/enums/user-roles.enum'
/**
 * Defines a list of available application routes
 */
export const routes: Routes = [
  {
    path: '',
    component: HomeRedirectComponent
  },
  {
    path: 'capacity',
    children: [
      {
        path: ':workspace_slug/:event_slug/:id/all_zones',
        component: VenueCapacityPageComponent
      },
      {
        path: ':workspace_slug/:event_slug/:id/:zone_slug',
        component: VenueCapacityPageComponent
      }
    ]
  },
  {
    path: 'personas',
    canActivate: [AuthGuard, CurrentWorkspaceGuard],
    children: []
  },
  // TODO: Handle Micetribe admin, migrate events, dtcm section, invitation components
  // {
  //   path: 'wizard',
  //   canActivate: [AuthGuard, CurrentWorkspaceGuard],
  //   children: [
  //     {
  //       path: 'add',
  //       component: MicetribeWizardComponent
  //     },
  //     {
  //       path: 'update/:id/:personaId',
  //       component: MicetribeWizardComponent
  //     },
  //     {
  //       path: 'update/:id',
  //       component: MicetribeWizardComponent
  //     },
  //     {
  //       path: 'list',
  //       component: EventsTimelineComponent
  //     }
  //   ]
  // },
  // {
  //   path: 'event-landing',
  //   canActivate: [AuthGuard],
  //   children: [
  //     {
  //       path: 'event/:event_id/form/:persona_id',
  //       component: LandingPageComponent
  //     }
  //   ]
  // },
  {
    path: 'events',
    canActivate: [AuthGuard, CurrentWorkspaceGuard],
    canActivateChild: [RoleGuard],
    resolve: { industrySectors: industrySectorResolver },
    children: [
      {
        path: ':eventId/financial-dashboard',
        component: FinancialDashboardComponent
      },
      {
        path: 'list',
        component: EventsTimelineComponent
      },
      {
        path: 'archive/:isArchive',
        component: EventsTimelineComponent
      },
      {
        path: 'invitations/:id',
        component: InvitationsTrackingComponent
      },
      {
        path: 'invite/:id',
        component: InviteComponent
      },
      {
        path: ':id/venue-capacity',
        component: VenueCapacityPageComponent
      },
      {
        path: ':id/dtcm',
        component: DtcmSetupComponent,
        providers: [EventLegalService]
      },
      {
        path: ':id/dct',
        component: DCTSetupComponent,
        providers: [EventLegalService]
      },
      {
        path: '*',
        redirectTo: 'list',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'app-settings/profile',
    component: ProfileComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'app-settings',
    redirectTo: 'app-settings/profile',
    pathMatch: 'full'
  },
  {
    path: 'app-settings',
    component: SettingsPageComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'organization',
        component: OrganizationComponent
      },
      {
        path: 'delete-facebook-data',
        component: DeleteFacebookDataCheckComponent
      },
      {
        path: '',
        redirectTo: 'organization',
        pathMatch: 'full'
      },
      {
        path: '**',
        redirectTo: 'organization',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'workspaces',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'list',
        component: WorkspacesComponent
      },
      {
        path: 'almost-there',
        component: AlmostThereComponent,
        resolve: {
          countries: countriesResolver
        }
      },
      {
        path: 'organization-settings',
        component: OrganizationSettingsComponent,
        resolve: {
          industrySectors: industrySectorResolver
        },
        canActivate: [AuthGuard, CurrentWorkspaceGuard, RoleGuard],
        data: { denyRoles: [UserRoles.VIEWER] }
      },
      {
        path: ':workspaceId/organization-settings',
        redirectTo: ({ params, fragment }) => {
          const url = `/workspaces/organization-settings?workspace_id=${params['workspaceId']}`
          return fragment ? `${url}#${fragment}` : url
        }
      },
      {
        path: '**',
        redirectTo: 'list',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'auth/login',
    component: SsoCallbackComponent
  },
  {
    path: 'auth/confirm-email',
    component: ConfirmEmailAlertComponent
  },
  {
    path: 'auth',
    redirectTo: '/auth/login',
    pathMatch: 'full'
  },
  {
    path: 'subscription',
    children: [
      {
        path: 'billing',
        component: BillingRedirectComponent
      },
      {
        path: 'success',
        component: SuccessfulUpgradeComponent
      },
      {
        path: 'cancel',
        component: CancelledUpgradeComponent
      }
    ]
  },
  {
    path: 'admin',
    canActivate: [AuthGuard, AdminGuard],
    children: [
      { path: 'statistics', component: StatisticsComponent },
      { path: 'tools', component: ToolsComponent, providers: [BadgeEditorService] },
      {
        path: 'trust-center',
        loadComponent: () => import('./app/v2/trust-center/components/trust-center-page/trust-center-page.component').then((m) => m.TrustCenterPageComponent),
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          {
            path: ':article',
            loadComponent: () => import('./app/v2/trust-center/components/trust-center-article/trust-center-article.component').then((m) => m.TrustCenterArticleComponent)
          }
        ]
      },
      { path: '', redirectTo: 'statistics', pathMatch: 'full' }
    ]
  },
  {
    path: 'trust-center/verify',
    loadComponent: () => import('./app/v2/trust-center/components/trust-center-public-verify/trust-center-public-verify.component').then((m) => m.TrustCenterPublicVerifyComponent)
  },
  {
    path: 'v2',
    loadChildren: () => import('./app/v2/v2.routes').then((m) => m.v2Routes)
  },
  {
    path: 'not-found',
    component: NotFoundComponent
  },
  {
    path: '**',
    redirectTo: 'not-found',
    pathMatch: 'full'
  }
]

/** Defines a global routing configuration options */
const config: ExtraOptions = {
  useHash: false,
  onSameUrlNavigation: 'reload'
  // enableTracing: true
}

/**
 * Main routing module
 */
@NgModule({
  imports: [RouterModule.forRoot(routes, config)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
